import { SHOP_IS_ACTIVE } from '@/consts/enum';
import { OfflineShop } from '@/types/naver-review';

import AppApi from './app-api';

export class OfflineShopsApi {
  constructor(private api: AppApi) {}

  async getShops(params: GetUserShopParams & { user_id: string }) {
    try {
      const res = await this.api.INSTANCE.get(`/users/${params.user_id}/offline-shops`, {
        params: {
          is_only_active: params.is_only_active,
        },
      });

      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async createShop(user_id: string, { crawl, ...body }: CreateOfflineShopDto) {
    try {
      const res = await this.api.INSTANCE.post(
        `/users/${user_id}/offline-shops${crawl ? '?crawl=true' : ''}`,
        body,
      );
      return res.data as OfflineShop;
    } catch (e) {
      throw e;
    }
  }

  async crawlingShop(user_id: string, offline_shop_id: string) {
    try {
      const res = await this.api.INSTANCE.post(`/users/${user_id}/offline-shops/request-fetch`, {
        offline_shop_id,
      });
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async createShopBatch(user_id: string, createShopDto: CreateOfflineShopDto[]) {
    try {
      const res = await this.api.INSTANCE.post(
        `/users/${user_id}/offline-shops/batch-create`,
        createShopDto,
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async registerBatch(user_id: string, registerBatchOfflineShopsDto: RegisterBatchOfflineShopsDto) {
    try {
      const res = await this.api.INSTANCE.post(
        `/users/${user_id}/offline-shops/batch-register`,
        registerBatchOfflineShopsDto,
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  }
  async requestCrawlingBatch(
    user_id: string,
    registerBatchOfflineShopsDto: RegisterBatchOfflineShopsDto,
  ) {
    try {
      const res = await this.api.INSTANCE.post(
        `/users/${user_id}/offline-shops/batch-request`,
        registerBatchOfflineShopsDto,
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async updateShop(params: UpdateOfflineShopParamsDto, updateShopDto: UpdateOfflineShopBodyDto) {
    try {
      const res = await this.api.INSTANCE.put(
        `/users/${params.user_id}/offline-shops/${params.offline_shop_id}`,
        updateShopDto,
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async deleteShop(deleteOfflineShopParamsDto: DeleteOfflineShopParamsDto) {
    try {
      const res = await this.api.INSTANCE.delete(
        `/users/${deleteOfflineShopParamsDto.user_id}/offline-shops/${deleteOfflineShopParamsDto.offline_shop_id}?hard_delete=${deleteOfflineShopParamsDto.hard_delete ?? true}`,
      );
      return res.data;
    } catch (e) {
      throw e;
    }
  }

  async batchUpdateShop({
    user_id,
    batchUpdateOfflineShopsDto,
  }: {
    user_id: string;
    batchUpdateOfflineShopsDto: BatchUpdateOfflineShopsDto;
  }) {
    try {
      const res = await this.api.INSTANCE.patch(`/users/${user_id}/offline-shops/batch-update`, {
        data: batchUpdateOfflineShopsDto,
      });
      return res.data;
    } catch (e) {
      throw e;
    }
  }
}

export interface CreateOfflineShopDto {
  shop_name: string;
  shop_address?: string;
  category?: string;
  url?: string;
  crawl?: boolean;
}

export interface UpdateOfflineShopBodyDto {
  shop_name?: string;
  shop_address?: string;
  place_id?: string;
  status?: string;
  is_active?: SHOP_IS_ACTIVE;
  priority?: number;
}

export interface UpdateOfflineShopParamsDto {
  user_id: string;
  offline_shop_id: string;
}

export interface DeleteOfflineShopParamsDto extends UpdateOfflineShopParamsDto {
  hard_delete?: boolean;
}

export interface GetUserShopParams {
  is_only_active?: boolean;
}

export interface RegisterBatchOfflineShopsDto {
  offline_shop_ids: string[];
}

export interface RequestCrawlingBatchOfflineShopsDto extends RegisterBatchOfflineShopsDto {}

export type BatchUpdateOfflineShopsDto = UpdateOfflineShopBodyDto[];

export default OfflineShopsApi;
